import { useMemo } from "react";
import type { Activity } from "@libs/types/access-live";
import { useAuth } from "@utils/firebaseAuth/auth";

export const useLiveRoomActivity = (
  activities: Activity[] | null | undefined,
) => {
  const { firebaseUser } = useAuth();
  const userId = firebaseUser?.uid;

  // Get current activity (most recent active/countdown, or most recent completed)
  const currentActivity = useMemo(() => {
    if (!activities || activities.length === 0) return null;

    // Find active or countdown activity first
    const activeActivity = activities.find(
      (a) => a.status === "active" || a.status === "countdown",
    );
    if (activeActivity) return activeActivity;

    // Otherwise return most recent completed
    const completedActivities = activities
      .filter((a) => a.status === "completed")
      .sort((a, b) => {
        const aTime =
          typeof a.completed_at === "string"
            ? new Date(a.completed_at).getTime()
            : (a.completed_at as any)?.toMillis?.() || 0;
        const bTime =
          typeof b.completed_at === "string"
            ? new Date(b.completed_at).getTime()
            : (b.completed_at as any)?.toMillis?.() || 0;
        return bTime - aTime;
      });

    return completedActivities[0] || null;
  }, [activities]);

  // Check if user is a winner in any completed activity
  // Exclude choose_the_cover as it has no individual winners
  const userWonActivities = useMemo(() => {
    if (!activities || !userId) return [];
    return activities.filter(
      (activity) =>
        activity.status === "completed" &&
        activity.type !== "choose_the_cover" &&
        activity.winners.includes(userId),
    );
  }, [activities, userId]);

  // Check if user is winner/loser in current activity
  const isWinner = useMemo(() => {
    if (!currentActivity || !userId) return false;
    return (
      currentActivity.status === "completed" &&
      currentActivity.winners.includes(userId)
    );
  }, [currentActivity, userId]);

  const isLoser = useMemo(() => {
    if (!currentActivity || !userId) return false;
    return (
      currentActivity.status === "completed" &&
      !currentActivity.winners.includes(userId)
    );
  }, [currentActivity, userId]);

  return {
    isWinner,
    isLoser,
    activity: currentActivity,
    activities: activities || [],
    userWonActivities,
  };
};
